import { error } from '@pressreader/logger';

import { getIdentityCommitPredicate, notifyOnIdentityCommitPredicateSet } from '../identity.clock';
import { IDENTITY_CHANGED } from '../identity.scope';
import { isIdentityClockWired, readIdentity } from '../identity.snapshot';
// The one non-type import from the listener module: the standing commit listener this module arms
// (`armIdentityCommitReconcile`) needs the shared `startAppListening`.
import { type AppThunkDispatch, startAppListening } from '../listener.middleware';

import type { IdentityPolicy, IdentityPolicyApi, IdentityPurgeEmpty } from './contract';
import { registry, reportUndeclaredQueryEndpoints } from './registry';
import { apiAccess, letJoinedConsumersSettle, type RunningQuery } from './rtk.access';

/** Identity generation the last completed reconcile pass ran under — see the dedupe in `runIdentityCommitReconcile`. */
let lastReconciledGeneration: number | undefined;

/**
 * Reconcile every registered api's caches to the identity the token now carries. Driven by the centralized
 * commit listener `armIdentityCommitReconcile` arms below — it lives HERE, and all it takes from
 * `@pressreader/authentication-core` is the injected predicate (`setIdentityCommitPredicate`) that recognizes a
 * commit, which is also what relays the identity saga's `identityReconcileRequested` for the transitions that
 * never commit. Features add nothing per feature.
 *
 * DEDUPED BY GENERATION, not by trigger: both triggers fire for the same transition (the commit first, the
 * saga's request after its settling delay), and the second must not repeat the purge + refetch. The generation
 * is the identity's own version, so this stays correct where a key compare cannot: a commit-less A -> B -> A
 * burst ends on the same key it started on, two generations later, and still reconciles. While no clock is
 * wired the generation is a constant, so the dedupe is disabled outright rather than collapsing every
 * transition into one (fail open, as everywhere else on this path).
 *
 * Per api, EVERY repair is the same direct one: each cached entry of a non-invariant endpoint is force-refetched
 * by `initiate`, key by key (see `repairEndpoint`). Purge-class entries are emptied synchronously first — the
 * security-relevant step; revalidate-class ones are not, so their data stays visible until the refetch replaces
 * it. Invariant endpoints are not touched at all.
 *
 * NO TAG INVALIDATION happens here, by design. Invalidating a tag TYPE is the broadest instrument RTKQ has, and
 * it reaches every endpoint that provides that type — including an `invariant` one that happens to share it,
 * which would refetch data this module promises to leave alone. A per-endpoint policy model cannot be enforced by
 * a type-level instrument, so the reconcile does not use one: it walks the endpoints it has a policy for.
 *
 * An endpoint present on the api with NO declared policy is a bug (its user-scoped data would survive the
 * transition): dev/test THROW before anything is patched; production logs loudly and repairs it the same direct
 * way, so it at least refetches — the residual is that its stale data stays visible until that refetch lands
 * (the price of staying on public RTKQ API — there is no public per-entry removal).
 */
export function runIdentityCommitReconcile(dispatch: AppThunkDispatch, getState: () => unknown): void {
    const { generation } = readIdentity();

    if (isIdentityClockWired() && generation === lastReconciledGeneration) {
        return;
    }

    for (const [api, policies] of registry) {
        if (isApiInStore(api, getState())) {
            reconcileApi(api, policies, dispatch, getState);
        }
    }

    // Marked only after a full pass: the dev/test throw for an undeclared endpoint must leave the generation
    // unhandled, so the next trigger for it runs (and throws) again rather than silently skipping.
    lastReconciledGeneration = generation;
}

/** Whether the commit reconcile listener has been registered — module-level, because the registration is app-wide. */
let commitReconcileArmed = false;

/** Whether the deferred-arming report has already gone out — one per session, like the unwired clock's. */
let deferredArmingReported = false;

/**
 * Arm the standing listener that runs `runIdentityCommitReconcile` when the app commits an identity change.
 *
 * Idempotent, and called from `declareIdentityPolicy`, so a feature's declaration is the whole subscription (see
 * there). `@pressreader/authentication-core`'s `registerAuthReconcile` calls it too, for a structural ordering
 * guarantee rather than for the arming: listener effects run in registration order, so getting the commit
 * reconcile registered before a caller's own feature-level listener is what makes the policy-driven reconcile run
 * first when both react to the same action.
 *
 * The predicate is not this module's to write — recognizing a commit means reading the auth slice, which appstore
 * cannot import — so it is injected at auth bootstrap via `setIdentityCommitPredicate`. Called before that
 * injection, this arming is DEFERRED rather than dropped: it reports the window once (a dead commit listener is a
 * silent loss of protection, so it must not be silent) and asks to be re-run at injection time, which is what
 * makes "a declaration is the whole subscription" true whichever of the two lands first.
 *
 * The residual, stated rather than glossed: a caller that arms before the injection has its OWN listener (if it
 * registers one, as `registerAuthReconcile` does) already in place when the retry fires, so on that ordering the
 * central listener registers BEHIND it and the feature's callbacks would run first. Acceptable, because the auth
 * bootstrap is eager and precedes every lazy feature in this app — the retry exists so the reconcile still HAPPENS
 * under any ordering, not to reorder what a reversed one produced.
 */
export function armIdentityCommitReconcile(): void {
    if (commitReconcileArmed) {
        return;
    }
    const predicate = getIdentityCommitPredicate();
    if (!predicate) {
        reportDeferredArming();
        // Completed by the injection itself, so nothing has to declare a second time for the listener to exist.
        notifyOnIdentityCommitPredicateSet(armIdentityCommitReconcile);
        return;
    }
    commitReconcileArmed = true;
    startAppListening({
        predicate,
        effect: (_action, { dispatch, getState }) => {
            runIdentityCommitReconcile(dispatch, getState);
        },
    });
}

/**
 * Report the deferred-arming window ONCE per session.
 *
 * Here rather than in `../identity.clock` on purpose: that module is the eager seam the auth bootstrap imports, and
 * it stays free of the logger for exactly that reason (as it does of RTK). This module is lazy, so the message —
 * and the string it carries — costs the eager bundle nothing.
 */
function reportDeferredArming(): void {
    if (deferredArmingReported) {
        return;
    }
    deferredArmingReported = true;
    error(
        new Error(
            'appstore: identity commit-reconcile arming deferred — no commit predicate injected yet (setIdentityCommitPredicate). Until injection happens, cached user-scoped data survives identity commits; only the fence-rejection repair backstop is active.',
        ),
    );
}

/**
 * Test-only seam: forget which generation was last reconciled, so a spec can drive the executor repeatedly
 * without the dedupe swallowing the second call. The marker is module-level (it has to outlive any one store).
 * Never call it from production code.
 */
export function __resetIdentityReconcileDedupeForTests(): void {
    lastReconciledGeneration = undefined;
}

/**
 * Test-only seam: un-arm the commit reconcile listener, so each case can arm it against its own store. Pair it
 * with `listenerMiddleware.clearListeners()` — this clears the module flag, not the registration RTK is holding.
 * Never call it from production code.
 */
export function __resetIdentityCommitReconcileForTests(): void {
    commitReconcileArmed = false;
}

function reconcileApi(api: IdentityPolicyApi, policies: Map<string, IdentityPolicy>, dispatch: AppThunkDispatch, getState: () => unknown): void {
    // The api's util thunks/actions are typed against RTK's own RootState/dispatch generics, which cannot
    // line up with this store's untyped state (see AppThunkDispatch) — one cast keeps call sites clean.
    const dispatchUtil = dispatch as (action: unknown) => unknown;

    // The undeclared sweep runs FIRST so a dev/test throw leaves the cache untouched rather than half-repaired. In
    // production it returns the gap instead of throwing, and each of those endpoints is repaired with NO recipe —
    // refetch without emptying, the only thing an undeclared endpoint can be given (no policy means no declared
    // `empty` to write), and the same treatment the revalidate contract asks for.
    for (const endpointName of reportUndeclaredQueryEndpoints(api, policies)) {
        repairEndpoint(api, endpointName, undefined, dispatchUtil, getState);
    }

    for (const [endpointName, policy] of policies) {
        if (policy.kind === 'invariant') {
            continue;
        }
        repairEndpoint(api, endpointName, policy.kind === 'purge' ? policy.empty : undefined, dispatchUtil, getState);
    }
}

/**
 * Repair every cached entry of ONE endpoint: force-refetch each of them, emptying first when a `recipe` says to.
 *
 * The one repair mechanism for all three cases the reconcile has — purge (with a recipe), revalidate (without),
 * and the production backstop for an endpoint nobody declared (also without, since it has no declared `empty`).
 * What separates them is that one argument, which is the whole of the difference between "the previous identity's
 * data must stop being readable NOW" and "it may stay visible until the refetch replaces it".
 *
 * Refetching per cached key rather than invalidating a tag is what keeps the policy PER ENDPOINT: a tag type
 * reaches every endpoint that provides it, an `initiate` reaches exactly this one. It also sidesteps
 * `invalidationBehavior: 'delayed'`, which holds an invalidation back while any request is in flight — that
 * deferral is what used to leave a purged entry resting at fulfilled-and-empty, indistinguishable from a
 * genuinely empty result. `forceRefetch` gets past the condition dedupe that would otherwise join the
 * already-fulfilled entry, and the entry flips to pending synchronously, restoring the `data` empty AND
 * `isFetching` contract consumers are documented against. A plain `initiate()` arriving after that (a one-shot
 * accessor) then dedupes onto this in-flight refetch instead of reading the empty — true of the QUIET path, where
 * the refetch is in flight from the same tick as the patch. On a busy key the refetch cannot start that early, and
 * an accessor arriving before it gets a rejection rather than either the empty or the new data;
 * `letJoinedConsumersSettle` is what makes that the outcome instead of a false success.
 *
 * `subscribe: false` — this is a repair, not a consumer: it must not hold an entry alive. The cost is that an
 * entry with no subscribers is refetched once (instead of being removed, as an invalidation would have done) and
 * then ages out on its own `keepUnusedDataFor` — a request for data nobody is reading, accepted for every class
 * alike in exchange for closing the stale window on the entries somebody IS reading. There is no public API to
 * tell the two apart.
 *
 * A key with a request ALREADY IN FLIGHT is the one case `forceRefetch` cannot repair on its own, so it is
 * handled separately (see `refetchAfterAbort`): RTKQ's query condition bails out on a pending cache key BEFORE
 * it looks at `forceRefetch`, so the forced refetch would silently JOIN the in-flight request instead of
 * starting a fresh one — and that request was started by the identity being left, so the fence converts its
 * settle into `IDENTITY_CHANGED`. The entry would be left rejected, holding the empty patch on a purge, with
 * nothing left to repair it — see `registerIdentityRejectionRepair` for why that end state is terminal, and for
 * the standing backstop that covers it arriving by any route this pass cannot see.
 */
function repairEndpoint(
    api: IdentityPolicyApi,
    endpointName: string,
    recipe: IdentityPurgeEmpty | undefined,
    dispatchUtil: (action: unknown) => unknown,
    getState: () => unknown,
): void {
    const { endpoints, util } = apiAccess(api);
    // Only entries that exist can be repaired, which is exactly what selectCachedArgsForQuery lists.
    for (const arg of util.selectCachedArgsForQuery(getState(), endpointName)) {
        // Read BEFORE the patch: anything in flight for this key belongs to the identity being left.
        const running = dispatchUtil(util.getRunningQueryThunk(endpointName, arg)) as RunningQuery | undefined;
        if (recipe) {
            dispatchUtil(util.updateQueryData(endpointName, arg, recipe));
        }
        const refetch = () => dispatchUtil(endpoints[endpointName].initiate(arg, { subscribe: false, forceRefetch: true }));
        if (running) {
            void refetchAfterAbort(running, refetch);
            continue;
        }
        refetch();
    }
}

/**
 * Displace the request in flight for one purged key: abort it, wait for it to settle, and only then force the
 * refetch (see `purge` for why joining it instead would strand the entry).
 *
 * The abort is thunk-level — the wire request's result is discarded, which costs nothing: the fence would have
 * rejected it anyway as belonging to the previous identity.
 *
 * It is aborted WITH A REASON, and the reason is the standardized `IDENTITY_CHANGED` status. RTKQ reduces an
 * aborted thunk into `{ name: 'AbortError', message: <reason> }`, which is the error a consumer joined to this
 * request is handed — and an abort is the one identity-crossing outcome the fence never sees, because the abort
 * discards the settle before it can be fenced. Naming the reason is what keeps that outcome classifiable:
 * `isIdentityChangedError` recognizes this shape too, so a consumer treats it as "still loading, retry after the
 * commit" instead of as a service failure it has to report.
 *
 * The AWAIT is load-bearing, not politeness. Aborting resolves the request one MICROTASK later, so the cache
 * key is still `pending` for the rest of the current tick: a forced refetch dispatched synchronously after the
 * abort would join the very request being displaced. Awaiting the promise (which resolves, never rejects, even
 * on abort — and only after RTKQ has reduced the rejection) is what guarantees the key is free.
 *
 * The extra `letJoinedConsumersSettle` hop after it is load-bearing for a different reason — see there.
 *
 * Fire-and-forget from the synchronous purge loop, so `runIdentityCommitReconcile` stays synchronous for its
 * listener-effect and saga callers. Safe: nothing downstream awaits the repair, and a second identity
 * transition arriving mid-chain needs no special handling — the chained refetch runs under whatever identity is
 * CURRENT when it is dispatched (joining that identity's own in-flight request if it has one, which is the
 * correct outcome), and the fence plus the generation dedupe own any further movement from there.
 */
async function refetchAfterAbort(running: RunningQuery, refetch: () => void): Promise<void> {
    running.abort(IDENTITY_CHANGED);
    await running;
    await letJoinedConsumersSettle();
    refetch();
}

function isApiInStore(api: IdentityPolicyApi, state: unknown): boolean {
    // A declared api whose reducer is not attached (enableSharedRtk not called yet) has no cache to
    // reconcile — and reading its slice would throw.
    return typeof state === 'object' && state !== null && api.reducerPath in state;
}
